import type { FC } from 'react'
import React from 'react'
import classNames from 'classnames'
import { GetClientPublicationsQueryParamsType, useGetClientPublications } from '@/api/kubik/infoportal'
import { JournalSearchInput, RubricsMenu } from '@/components/composed'
import { Articles, NewsPreview } from '@/components/resolvers'
import { MetaInfo, PaginationMoreButton } from '@/components/ui'
import { formatLocalizedString } from '@/utils/formatUtils'
import { useInfiniteJournalPublications } from '@/utils/infoportal/hooks/useInfiniteJournalPublications'
import { useJournalRubrics } from '@/utils/infoportal/hooks/useJournalRubrics'
import { EJournalClickEventBlockId, JournalGtmClickEventPayload } from '../../gtmConstants'
import { openArticleClicked } from '../../gtmHelpers'
import { messages } from './messages'
import type { IJournalByRubricsProps } from './types'
import './styles.scss'

const JournalByRubrics: FC<IJournalByRubricsProps> = props => {
  const { rubricId } = props

  const {
    publications: articles,
    isPublicationsLoading: isArticlesLoading,
    hasNextPage,
    getNextPage,
  } = useInfiniteJournalPublications({
    type: GetClientPublicationsQueryParamsType.ARTICLE,
    rubricId: Number(rubricId),
  })

  const {
    data: newsData,
    isLoading: isNewsLoading,
    isSuccess: isNewsLoaded,
    isError: isNewsFailed,
  } = useGetClientPublications(
    { type: GetClientPublicationsQueryParamsType.NEWS, includeMain: true, limit: 5, rubricId: Number(rubricId) },
    { query: { staleTime: Infinity } },
  )

  const { currentRubric } = useJournalRubrics(rubricId)

  const { publications: news = [] } = newsData?.data || {}

  const shouldShowNews = !currentRubric?.special && ((!isNewsLoaded && !isNewsFailed) || news.length > 0)
  const shouldShowArticlesHeader = !currentRubric?.special

  return (
    <>
      {currentRubric && (
        <MetaInfo
          metaInfo={{
            title: formatLocalizedString(messages.reactHelmetTitle, { rubricName: currentRubric.name }),
          }}
        />
      )}

      <RubricsMenu />
      <JournalSearchInput />

      <div className={classNames('journalByRubrics__header', { 'journalByRubrics__header--special': !!currentRubric?.special })}>
        {currentRubric && <h1 className="page-wrapper journalByRubrics__rubricTitle">{currentRubric.name}</h1>}

        {shouldShowNews && (
          <NewsPreview
            rubric={currentRubric}
            news={news}
            isLoading={isNewsLoading}
            onClick={openArticleClicked(JournalGtmClickEventPayload.listArticles)}
          />
        )}
      </div>
      <div className="journalByRubrics__articles page-wrapper">
        {shouldShowArticlesHeader && <h2 className="journalArticles__title">{messages.articlesHeader}</h2>}
        <Articles
          articles={articles}
          isArticlesLoading={isArticlesLoading}
          blockId={EJournalClickEventBlockId.listArticles}
          onClick={openArticleClicked(JournalGtmClickEventPayload.listArticles)}
        />

        <PaginationMoreButton
          className="journal__loadMoreBtn"
          onClick={getNextPage}
          isLoading={isArticlesLoading}
          hasNextPage={hasNextPage}
        />
      </div>
    </>
  )
}

export default JournalByRubrics
